import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { FS, SP, SHADOWS } from '../../theme/tokens';
import { v2Tone } from '../../theme/theme';
import { useServiceOutage } from '../../serviceOutage/ServiceOutageProvider';

/**
 * ServiceOutageBanner — die EINE Meldung, wenn der Dienst als Ganzes steht.
 *
 * @description
 *   Waehrend eines Deploys endet jeder `/api/`-Aufruf kurz mit 502/503/504 oder
 *   einem Netzfehler. Ohne diesen Baustein zeigt jede Flaeche ihre eigene
 *   Fehlerform (das Foto des Owners: FUENF Formen auf einer Seite). Der Banner
 *   fasst sie in EINE Aussage — er haengt am geteilten Erreichbarkeits-Waechter
 *   (`frontend/src/api/serviceOutage.js`) und steht fest ueber der Schale.
 *
 *   🔴 Er ist AUSDRUECKLICH KEIN `Alert severity="error"`. Die E-3-Theme-Regel
 *   blendet `MuiAlert`-`error`-Varianten im `main` waehrend eines Ausfalls aus;
 *   laege der Banner je in `main`, faenge er sich selbst. Er liegt deshalb
 *   ausserhalb von `main` (montiert in `App.js`) UND traegt keine `Alert`-Rolle.
 *
 *   Drei Zustaende, drei Aussagen: `ausfall` („Forge is restarting"), `offline`
 *   („No connection." — der Dienst LAEUFT, nur der Rechner hat kein Netz, E-11)
 *   und die kurze Erholung („Back online.").
 *
 *   🔴 R16-B1 (Nachbesserung 4, 2026-10-11): er VERSCHIEBT die Schale, statt sie
 *   zu verdecken. Bis dahin lag er `fixed` ueber der Kopfleiste, und deren
 *   Bedienelemente waren nicht nur verdeckt, sondern unerreichbar — ein
 *   Tastaturnutzer tabbt auf ein vollstaendig verdecktes Ziel (WCAG 2.2
 *   SC 2.4.11, AA). Der Banner misst darum seine eigene Hoehe
 *   (`ResizeObserver`) und setzt EINE CSS-Variable am Dokument
 *   (`--pog-banner-hoehe`, im Gruenen `0px`); `theme/theme.js` zieht Kopfleiste,
 *   Seitenleisten-Papier und Inhaltsfluss um genau diese Hoehe nach.
 *
 * @useWhen
 *   - Genau einmal, im App-Rahmen (`App.js`, ausserhalb `<Routes>`) — nie je Seite
 *
 * @dontUseFor
 *   - Ein Fehler EINER Flaeche — dafuer `EmptyState reason="error"` oder das Notice-Muster
 *   - Ein Teilausfall (einzelne Endpunkte rot, `/api/health` gruen) — dann bleibt es lokal
 *   - Eine eigene Ladefehler-Meldung in einer Seite — die Theme-Regel deckt sie schon
 *
 * @related
 *   - `frontend/src/api/serviceOutage.js` — der Erkenner (Zustandsmaschine + Sonde)
 *   - `frontend/src/serviceOutage/ServiceOutageProvider.jsx` — die React-Bindung, die diesen Banner speist
 *   - `EmptyState` — der lokale Fehler-/Leerzustand, den der Ausfall stillstellt
 *
 * @reuseHints
 *   - Der Nachzug Z-1c haengt `pog:service-online` an die betroffenen Flaechen
 *   - Ein „erneut versuchen"-Weg koennte `probiereJetzt()` aus dem Kontext rufen
 *
 * @props
 *   keine — der Baustein liest seinen Zustand aus `useServiceOutage()`.
 */

/** So lange steht die Eintritts-Ansage (`role="alert"`), dann faellt sie aus dem Baum. */
export const EINTRITT_MS = 1200;

/** So lange steht „Back online." nach der Erholung. */
export const ERHOLUNG_MS = 8000;

/**
 * 🔴 Die CSS-Variable, um die der Banner die Schale verschiebt (R16-B1).
 *
 * Sie steht am `documentElement` und wird in `theme/theme.js` gelesen: die
 * feste Kopfleiste, das feste Seitenleisten-Papier und der Inhaltsfluss ruecken
 * um genau diese Hoehe nach. Der Banner selbst setzt sie aus seiner eigenen
 * gemessenen Hoehe; im Gruenen steht `0px` — dann ist die Schale unveraendert.
 *
 * Der Name steht hier UND im Theme-Text (dort als Zeichenkette im CSS). Zwei
 * Orte, ein Name: wer ihn aendert, zieht beide nach.
 */
export const BANNER_HOEHE_VAR = '--pog-banner-hoehe';

/**
 * 🔴 Die Ebene des Banners — ABGELEITET, nicht als Zahl geschrieben.
 *
 * Der Banner liegt `fixed` am oberen Rand, also **ueber** der Schale: die
 * Kopfleiste steht auf `theme.zIndex.drawer + 2` (`SidebarAndHeaderBar.jsx:1255`,
 * gemessen 2026-10-10), die Seitenleiste auf `theme.zIndex.drawer` (1200).
 * Darueber, aber **unter** jedem Dialog/Drawer-Modal (`theme.zIndex.modal`,
 * 1300) — ein Dialog ist der Ort, an dem die Handlung noch sichtbar ist.
 *
 * 🔴 Eine feste Zahl (hier stand `theme.zIndex.appBar + 2` = 1102) ist auf dem
 * Desktop **unsichtbar geblieben**: 1102 < 1202 der Kopfleiste. Mobil lag der
 * Banner zufaellig darueber — dieselbe Zeile, zwei verschiedene Ergebnisse, je
 * nach Breite. Die Herleitung aus dem Theme macht den Abstand nachpruefbar.
 *
 * @param {{ zIndex: { modal: number } }} theme
 * @returns {number} die z-Ebene des Banners
 */
export const bannerEbene = (theme) => theme.zIndex.modal - 1;

/**
 * Der sichtbare Satz im Ausfall — Owner-Freigabe (Samuel, 2026-10-10), Wortlaut
 * woertlich. Titel und Rest stehen als EIN Textfluss (nicht als zwei
 * Flex-Kinder): sonst bricht der Banner auf schmalen Breiten in drei Zeilen um
 * (Titel / Rest / Knopf) und verdeckt Kopfleiste und Inhaltsanfang.
 */
const AUSFALL_TITEL = 'Forge is restarting';
const AUSFALL_REST =
  '(usually a deploy). This page will keep trying. Reload to see the latest data.';
const AUSFALL_SATZ = `${AUSFALL_TITEL} ${AUSFALL_REST}`;
const OFFLINE_TITEL = 'No connection.';
const OFFLINE_REST = 'This page will keep trying when you are back online.';
const ERHOLT_SATZ = 'Back online.';

export default function ServiceOutageBanner() {
  const { zustand } = useServiceOutage();
  const [eintritt, setEintritt] = useState(false);
  const [erholt, setErholt] = useState(false);
  const vorher = useRef(zustand);
  const kasten = useRef(null);

  useEffect(() => {
    const alt = vorher.current;
    vorher.current = zustand;
    let timer = null;

    if (zustand === 'ausfall') {
      // E-3b: die Eintritts-Ansage wird beim Uebergang EINMAL gerendert und
      // danach aus dem Baum genommen — so wird sie nicht doppelt angesagt.
      setEintritt(true);
      setErholt(false);
      timer = setTimeout(() => setEintritt(false), EINTRITT_MS);
    } else if (alt !== 'online' && zustand === 'online') {
      // Die Erholung ist keine Unterbrechung: role="status", nicht "alert".
      setEintritt(false);
      setErholt(true);
      timer = setTimeout(() => setErholt(false), ERHOLUNG_MS);
    } else {
      setEintritt(false);
      setErholt(false);
    }

    return () => { if (timer) clearTimeout(timer); };
  }, [zustand]);

  // 🔴 R16-B1: die eigene Hoehe messen und als EINE Variable ans Dokument geben.
  // Der Effekt haengt an `sichtbar`/`zustand`, weil der Kasten im Gruenen gar
  // nicht im Baum steht (der fruehe `return null` oben) — ohne die Abhaengigkeit
  // bliebe die Variable nach der Erholung auf der alten Hoehe stehen.
  const sichtbar = zustand !== 'online' || erholt;

  // 🔴 R15-W3 (Nachbesserung 6, 2026-10-11): `useLayoutEffect` statt `useEffect`.
  // Ein `useEffect` laeuft NACH dem Paint — im ersten Bild nach dem Erscheinen
  // stuende der Banner einen Frame `fixed; top: 0` ueber einer Schale, die noch
  // auf `top: 0` steht (der Rest des WCAG-2.4.11-Befunds). `useLayoutEffect`
  // laeuft VOR dem Paint und schliesst das Fenster. Die App ist eine CRA ohne
  // SSR; die `document`-Wache darunter bleibt trotzdem stehen.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const setze = (hoehe) => {
      document.documentElement.style.setProperty(BANNER_HOEHE_VAR, `${Math.round(hoehe)}px`);
    };
    const el = kasten.current;
    if (!sichtbar || !el) {
      // Gruener Zustand: die Schale steht unveraendert. AUSDRUECKLICH `0px`
      // statt `removeProperty` — die Variable ist damit in JEDEM Zustand
      // lesbar, und die Probe kann „gesetzt" von „nie gesetzt" unterscheiden.
      setze(0);
      return undefined;
    }
    const messen = () => setze(el.getBoundingClientRect().height);
    messen();

    // Ohne `ResizeObserver` (jsdom, sehr alte Browser) bleibt es bei der ersten
    // Messung — der Banner wechselt dann nur zwischen zwei Wortlauten, und der
    // Unterschied wird beim naechsten Zustandswechsel nachgemessen.
    if (typeof ResizeObserver === 'undefined') {
      return () => setze(0);
    }
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => {
      beobachter.disconnect();
      setze(0);
    };
  }, [sichtbar, zustand]);

  if (zustand === 'online' && !erholt) return null;

  const ausfall = zustand === 'ausfall';
  const offline = zustand === 'offline';

  const neuLaden = () => {
    try { window.location.reload(); } catch { /* kein Fenster — nichts zu tun */ }
  };

  return (
    <Box
      ref={kasten}
      data-testid="service-outage-banner"
      sx={(theme) => {
        // 🔴 R16-W3: die v2-Toene ueber `v2Tone`, wie die Geschwister (`Pill`,
        // `StatTile`, `AgingBar`, `ProvenanceMark`). `theme.palette.v2.…` direkt
        // zu lesen ist die EINZIGE solche Stelle unter `components/common/` und
        // WIRFT unter einem Theme ohne die Skala (`createTheme()` blank) —
        // `v2Tone` faellt dann auf die Skala des Modus zurueck.
        const ton = v2Tone(theme, ausfall ? 'warn' : 'info');
        return {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: bannerEbene(theme),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // 🔴 R16-B1, mobil: KEIN Umbau zu einem drei Blocke hohen Kasten
          // (~120 px, Kopfbereich verdeckt). Auf `xs` bleibt EINE Zeile: der
          // Satz bricht IN sich um, der Knopf steht daneben — der Kasten bleibt
          // unter `BANNER_MAX_MOBIL` (`ui2-teil9-ausfall.spec.ts`).
          // 🔴 R16-W3 (Nachbesserung 6, 2026-10-11): `FS.sm` auf BEIDEN Breiten.
          // Der Ausfallsatz ist im Ausfall die EINZIGE Aussage der Seite; `FS.xs`
          // ist fuer Eyebrows und dichte Chip-Etiketten definiert
          // (`design-tokens.md`), nicht fuer eine Statuszeile. Der Kasten darf
          // dafuer hoeher werden — die Schranke bleibt `BANNER_MAX_MOBIL`.
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: { xs: SP.tight, sm: SP.card },
          px: { xs: SP.tight, sm: SP.card },
          py: { xs: SP.chip, sm: SP.tight },
          fontSize: FS.sm,
          bgcolor: ton.soft,
          color: ton.main,
          borderBottom: `1px solid ${ton.c100}`,
          boxShadow: SHADOWS.panel,
        };
      }}
    >
      {eintritt && (
        <Box component="span" role="alert" sx={visuallyHidden}>
          {AUSFALL_SATZ}
        </Box>
      )}

      {ausfall && (
        <>
          {/* 🔴 Titel und Rest als EIN Textfluss (`flex: 0 1 auto`, `minWidth: 0`):
              der Text bricht IN sich um, statt den Knopf in eine eigene Zeile zu
              draengen. Der Knopf steht daneben, solange Platz ist (`flexWrap`). */}
          <Typography
            component="p"
            data-testid="service-outage-text"
            sx={{ m: 0, flex: '0 1 auto', minWidth: 0 }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              {AUSFALL_TITEL}
            </Box>{' '}
            {AUSFALL_REST}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={neuLaden}
            sx={{ flexShrink: 0 }}
          >
            Reload
          </Button>
        </>
      )}

      {offline && (
        // 🔴 R16-W2: der Offline-Fall wird ANGESAGT — aber hoeflich
        // (`role="status"`/`aria-live="polite"`), NICHT als `alert`. Ein
        // Laptop ohne Netz ist keine Unterbrechung; die Ansagepflicht aus E-3b
        // gilt fuer den Ausfall, hier fehlte sie ganz.
        <Typography
          component="p"
          role="status"
          aria-live="polite"
          data-testid="service-outage-text"
          sx={{ m: 0, flex: '0 1 auto', minWidth: 0 }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {OFFLINE_TITEL}
          </Box>{' '}
          {OFFLINE_REST}
        </Typography>
      )}

      {erholt && (
        <>
          <Typography component="span" role="status" aria-live="polite" sx={{ fontWeight: 600 }}>
            {ERHOLT_SATZ}
          </Typography>
          {/* 🔴 R16-W5: derselbe Knopf im Erholungsfenster. Bis dahin verschwand
              „Reload" MIT dem Ausfall — also genau dann, wenn die gescheiterten
              Abrufe als Fehlerwand stehenbleiben und er nuetzen wuerde.
              Freigegebener Wortlaut, kein neuer Text. */}
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={neuLaden}
            sx={{ flexShrink: 0 }}
          >
            Reload
          </Button>
        </>
      )}
    </Box>
  );
}
